import React from "react";
import { Eye } from "lucide-react";
import type { Ok } from "@ps-bridge/ok";
import type { OptionTs } from "@ps-bridge/interop";
import type {
  ContextPreviewCitation,
  ContextPreviewPurpose,
  ContextPreviewResult,
} from "@services/clients/DocumentAssistantRetrievalContextPreviewApi";
import type {
  SupplyAcceptedResponse,
  SupplyStatusResponse,
} from "@services/clients/DocumentAssistantSupplyApi";
import * as Option from "@utils/Option";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";
import EmptyView from "@view/EmptyView";
import * as styles from "@styles/ContextPreviewConsole.css";

export type ContextPreviewConsolePhase =
  | "idle"
  | "loading"
  | "success"
  | "readDenied"
  | "malformed"
  | "missingWorkspace"
  | "sendRefused"
  | "error";

export type ContextPreviewSupplyPhase =
  | "idle"
  | "uploading"
  | "accepted"
  | "polling"
  | "statusLoaded"
  | "blocked"
  | "failed";

export type ContextPreviewConsoleForm = {
  readonly listingId: string;
  readonly workspaceId: string;
  readonly workspaceVersion: string;
  readonly purpose: ContextPreviewPurpose;
  readonly prompt: string;
  readonly sendRequestAttempted: boolean;
};

export type ContextPreviewSupplyForm = {
  readonly displayName: string;
  readonly sourceHandle: string;
  readonly statusSupplyId: string;
  readonly selectedDocument: OptionTs<File>;
};

export type ContextPreviewConsolePanelProps = {
  readonly programName: string;
  readonly phase: ContextPreviewConsolePhase;
  readonly message: string;
  readonly form: ContextPreviewConsoleForm;
  readonly result: OptionTs<ContextPreviewResult>;
  readonly supplyPhase: ContextPreviewSupplyPhase;
  readonly supplyMessage: string;
  readonly supplyForm: ContextPreviewSupplyForm;
  readonly acceptedSupply: OptionTs<SupplyAcceptedResponse>;
  readonly supplyStatus: OptionTs<SupplyStatusResponse>;
  readonly onSubmit: (event: React.FormEvent<HTMLFormElement>) => Ok;
  readonly onFieldChange: <K extends keyof ContextPreviewConsoleForm>(
    field: K,
    value: ContextPreviewConsoleForm[K],
  ) => Ok;
  readonly onSupplyFieldChange: <K extends keyof ContextPreviewSupplyForm>(
    field: K,
    value: ContextPreviewSupplyForm[K],
  ) => Ok;
  readonly onSupplyUpload: () => Promise<Ok>;
  readonly onSupplyStatus: () => Promise<Ok>;
};

const pageStateByPhase = {
  idle: "ready",
  loading: "loading",
  success: "ready",
  readDenied: "ready",
  malformed: "ready",
  missingWorkspace: "ready",
  sendRefused: "ready",
  error: "ready",
} as const satisfies Record<ContextPreviewConsolePhase, "ready" | "loading">;

const buttonTextByPhase = {
  idle: "Preview Context",
  loading: "Previewing",
  success: "Refresh Preview",
  readDenied: "Retry Preview",
  malformed: "Retry Preview",
  missingWorkspace: "Retry Preview",
  sendRefused: "Retry Preview",
  error: "Retry Preview",
} as const satisfies Record<ContextPreviewConsolePhase, string>;

const statusClassByPhase = {
  idle: styles.status,
  loading: `${styles.status} ${styles.statusWarning}`,
  success: `${styles.status} ${styles.statusReady}`,
  readDenied: `${styles.status} ${styles.statusDanger}`,
  malformed: `${styles.status} ${styles.statusWarning}`,
  missingWorkspace: `${styles.status} ${styles.statusWarning}`,
  sendRefused: `${styles.status} ${styles.statusDanger}`,
  error: `${styles.status} ${styles.statusDanger}`,
} as const satisfies Record<ContextPreviewConsolePhase, string>;

const statusTitleByPhase = {
  idle: "Ready",
  loading: "Loading",
  success: "Preview Loaded",
  readDenied: "Read Denied",
  malformed: "Malformed Preview",
  missingWorkspace: "Reviewed Workspace Required",
  sendRefused: "Send Attempt Refused",
  error: "Preview Unavailable",
} as const satisfies Record<ContextPreviewConsolePhase, string>;

const supplyPageStateByPhase = {
  idle: "ready",
  uploading: "loading",
  accepted: "ready",
  polling: "loading",
  statusLoaded: "ready",
  blocked: "ready",
  failed: "ready",
} as const satisfies Record<ContextPreviewSupplyPhase, "ready" | "loading">;

const supplyStatusClassByPhase = {
  idle: styles.status,
  uploading: `${styles.status} ${styles.statusWarning}`,
  accepted: `${styles.status} ${styles.statusReady}`,
  polling: `${styles.status} ${styles.statusWarning}`,
  statusLoaded: `${styles.status} ${styles.statusReady}`,
  blocked: `${styles.status} ${styles.statusWarning}`,
  failed: `${styles.status} ${styles.statusDanger}`,
} as const satisfies Record<ContextPreviewSupplyPhase, string>;

const supplyStatusTitleByPhase = {
  idle: "Supply Ready",
  uploading: "Uploading Supply",
  accepted: "Supply Accepted",
  polling: "Reading Supply Status",
  statusLoaded: "Supply Status Loaded",
  blocked: "Supply Blocked",
  failed: "Supply Failed",
} as const satisfies Record<ContextPreviewSupplyPhase, string>;

const resultViewByPhase = (
  result: OptionTs<ContextPreviewResult>,
): Record<ContextPreviewConsolePhase, React.ReactElement> => ({
  idle: <EmptyView />,
  loading: <EmptyView />,
  success: Option.fold(result, () => <EmptyView />, (value) => <PreviewResultView result={value} />),
  readDenied: <EmptyView />,
  malformed: <EmptyView />,
  missingWorkspace: <EmptyView />,
  sendRefused: <EmptyView />,
  error: <EmptyView />,
});

const renderGaps = (gaps: readonly string[]): React.ReactElement => (
  <div className={styles.gapList}>
    {gaps.map((gap) => (
      <span className={styles.gapBadge} key={gap}>{gap}</span>
    ))}
  </div>
);

const renderCitationPriorListing = (citation: ContextPreviewCitation): string =>
  Option.renderOptionText(citation.priorListingId, "current listing", (value) => `prior listing ${value}`);

const renderCitationConfidence = (citation: ContextPreviewCitation): string =>
  Option.renderOptionText(citation.confidence, "confidence unavailable", (value) => `confidence ${value.toFixed(2)}`);

const selectedFileOption = (files: FileList): OptionTs<File> => {
  if (files.length === 0) {
    return Option.none<File>();
  }
  return Option.some(files[0] as File);
};

const PreviewResultView: React.FC<{ readonly result: ContextPreviewResult }> = ({ result }) => {
  const inferenceItemsPhase = enabledFeatures[FeaturesEnum.ContextPreviewInferences] ? "visible" : "hidden";
  const inferenceItemsView = {
    hidden: <EmptyView />,
    visible: (
      <section className={styles.section} aria-labelledby="context-preview-inferences">
        <h2 className={styles.sectionTitle} id="context-preview-inferences">Confirmed Inferences</h2>
        <div className={styles.list}>
          {result.confirmedInferences.map((inference) => (
            <article className={styles.row} key={inference.claimId} data-testid="context-preview-inference">
              <h3 className={styles.rowTitle}>{inference.value}</h3>
              <p className={styles.rowMeta}>{inference.kind} · {inference.disposition} · {inference.provenance.sourceRef}</p>
            </article>
          ))}
        </div>
      </section>
    ),
  } as const satisfies Record<"hidden" | "visible", React.ReactElement>;

  return (
    <div className={styles.result} data-testid="context-preview-result">
    <div className={styles.summaryGrid}>
      <div className={styles.metric}>
        <span className={styles.metricLabel}>Reviewed workspace</span>
        <span className={`${styles.metricValue} ${styles.mono}`}>{result.workspaceId}</span>
      </div>
      <div className={styles.metric}>
        <span className={styles.metricLabel}>Workspace version</span>
        <span className={styles.metricValue}>{result.workspaceVersion}</span>
      </div>
      <div className={styles.metric}>
        <span className={styles.metricLabel}>Freshness</span>
        <span className={styles.metricValue}>{result.cacheState.freshness}</span>
      </div>
      <div className={styles.metric}>
        <span className={styles.metricLabel}>sendAuthorized</span>
        <span className={styles.metricValue}>{String(result.readiness.sendAuthorized)}</span>
      </div>
    </div>

    <section className={styles.section} aria-labelledby="context-preview-sources">
      <h2 className={styles.sectionTitle} id="context-preview-sources">Sources</h2>
      <div className={styles.list}>
        {result.sources.map((source) => (
          <article className={styles.row} key={source.sourceId} data-testid="context-preview-source">
            <h3 className={`${styles.rowTitle} ${styles.mono}`}>{source.sourceId}</h3>
            <p className={styles.rowMeta}>revision {source.revision} · {source.granularity}</p>
            <p className={styles.rowMeta}>{source.audit}</p>
          </article>
        ))}
      </div>
    </section>

    <section className={styles.section} aria-labelledby="context-preview-citations">
      <h2 className={styles.sectionTitle} id="context-preview-citations">Citations</h2>
      <div className={styles.list}>
        {result.citations.map((citation) => (
          <article className={styles.row} key={`${citation.sourceId}-${citation.evidenceLabel}`} data-testid="context-preview-citation">
            <h3 className={styles.rowTitle}>{citation.sourceLabel} · {citation.evidenceLabel}</h3>
            <p className={styles.rowMeta}>{renderCitationPriorListing(citation)} · {citation.authority} · {renderCitationConfidence(citation)}</p>
            <p className={styles.rowMeta}>{citation.rationale}</p>
            {renderGaps(citation.authorityGaps)}
          </article>
        ))}
      </div>
    </section>

    <section className={styles.section} aria-labelledby="context-preview-evidence">
      <h2 className={styles.sectionTitle} id="context-preview-evidence">Evidence Summary</h2>
      <div className={styles.summaryGrid}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Current claims</span>
          <span className={styles.metricValue}>{result.evidenceSummary.current.claimCount}</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Prior listing claims</span>
          <span className={styles.metricValue}>{result.evidenceSummary.priorListings.claimCount}</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Manual assertions</span>
          <span className={styles.metricValue}>{result.authoritySummary.manualAssertionCount}</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Prior listing citations</span>
          <span className={styles.metricValue}>{result.authoritySummary.priorListingCitationCount}</span>
        </div>
      </div>
      {renderGaps(result.evidenceSummary.authorityGaps)}
    </section>

    {inferenceItemsView[inferenceItemsPhase]}

    <section className={styles.section} aria-labelledby="context-preview-unresolved">
      <h2 className={styles.sectionTitle} id="context-preview-unresolved">Unresolved Questions</h2>
      <div className={styles.list}>
        {result.unresolvedQuestions.map((question) => (
          <article className={styles.row} key={`${question.evidenceSource}-${question.question}`} data-testid="context-preview-unresolved">
            <h3 className={styles.rowTitle}>{question.question}</h3>
            <p className={styles.rowMeta}>{question.evidenceSource} · {question.provenance.speaker}</p>
          </article>
        ))}
      </div>
    </section>

    <section className={styles.section} aria-labelledby="context-preview-cache">
      <h2 className={styles.sectionTitle} id="context-preview-cache">Cache And Trace</h2>
      <div className={styles.list}>
        <article className={styles.row}>
          <h3 className={`${styles.rowTitle} ${styles.mono}`}>{result.cacheState.profileCacheKey}</h3>
          <p className={styles.rowMeta}>deletion notices {result.cacheState.deletionNoticeCount} · readOnly {String(result.readOnly)} · noSendReady {String(result.readiness.noSendReady)}</p>
          <p className={`${styles.rowMeta} ${styles.mono}`}>trace {result.trace.traceId} · request {result.trace.requestId}</p>
        </article>
      </div>
    </section>
    </div>
  );
};

const SupplyAcceptedView: React.FC<{ readonly accepted: SupplyAcceptedResponse }> = ({ accepted }) => (
  <div className={styles.supplyResult} data-testid="document-supply-accepted">
    <div className={styles.metric}>
      <span className={styles.metricLabel}>Accepted supply</span>
      <span className={`${styles.metricValue} ${styles.mono}`}>{accepted.supplyId}</span>
    </div>
    <div className={styles.metric}>
      <span className={styles.metricLabel}>Milestone</span>
      <span className={styles.metricValue}>{accepted.uploadStatus}</span>
    </div>
    <div className={styles.metric}>
      <span className={styles.metricLabel}>Indexing</span>
      <span className={styles.metricValue}>{accepted.indexing}</span>
    </div>
  </div>
);

const SupplyStatusView: React.FC<{ readonly status: SupplyStatusResponse }> = ({ status }) => (
  <div className={styles.supplyResult} data-testid="document-supply-status-result">
    <div className={styles.metric}>
      <span className={styles.metricLabel}>Status supply</span>
      <span className={`${styles.metricValue} ${styles.mono}`}>{status.supplyId}</span>
    </div>
    <div className={styles.metric}>
      <span className={styles.metricLabel}>Milestone</span>
      <span className={styles.metricValue}>{status.uploadStatus}</span>
    </div>
    <div className={styles.metric}>
      <span className={styles.metricLabel}>Retrieval</span>
      <span className={styles.metricValue}>{status.retrieval}</span>
    </div>
  </div>
);

const ContextPreviewConsolePanel: React.FC<ContextPreviewConsolePanelProps> = ({
  phase,
  programName,
  message,
  form,
  result,
  supplyPhase,
  supplyMessage,
  supplyForm,
  acceptedSupply,
  supplyStatus,
  onSubmit,
  onFieldChange,
  onSupplyFieldChange,
  onSupplyUpload,
  onSupplyStatus,
}) => {
  const resultView = resultViewByPhase(result);
  const supplyBusy = supplyPageStateByPhase[supplyPhase] === "loading";
  const acceptedView = Option.fold(
    acceptedSupply,
    () => <EmptyView />,
    (accepted) => <SupplyAcceptedView accepted={accepted} />,
  );
  const statusView = Option.fold(
    supplyStatus,
    () => <EmptyView />,
    (status) => <SupplyStatusView status={status} />,
  );

  return (
    <section
      className={styles.page}
      data-testid="page-context-preview-console"
      data-page-state={pageStateByPhase[phase]}
      data-program={programName}
    >
      <div className={styles.shell}>
        <header className={styles.header}>
          <p className={styles.eyebrow}>Retrieval</p>
          <h1 className={styles.title}>Context Preview Console</h1>
          <p className={styles.subtitle}>
            Inspect governed retrieval context for a listing and prompt before any drafting or sending workflow.
          </p>
          <div className={styles.workspaceLine}>
            <span className={styles.badge}>read-only</span>
            <span className={styles.badge}>sendAuthorized false</span>
          </div>
        </header>

        <div className={styles.layout}>
          <form className={styles.panel} onSubmit={onSubmit} data-testid="context-preview-form">
            <div className={styles.form}>
              <label className={styles.field}>
                <span className={styles.label}>Listing ID</span>
                <input
                  className={styles.input}
                  value={form.listingId}
                  onChange={(event) => onFieldChange("listingId", event.target.value)}
                  data-testid="context-preview-listing-id"
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Reviewed Workspace ID</span>
                <input
                  className={styles.input}
                  value={form.workspaceId}
                  onChange={(event) => onFieldChange("workspaceId", event.target.value)}
                  data-testid="context-preview-workspace-id"
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Workspace Version</span>
                <input
                  className={styles.input}
                  value={form.workspaceVersion}
                  onChange={(event) => onFieldChange("workspaceVersion", event.target.value)}
                  inputMode="numeric"
                  data-testid="context-preview-workspace-version"
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Purpose</span>
                <select
                  className={styles.input}
                  value={form.purpose}
                  onChange={(event) => onFieldChange("purpose", event.target.value as ContextPreviewPurpose)}
                  data-testid="context-preview-purpose"
                >
                  <option value="answerQuestion">Answer question</option>
                  <option value="draftContent">Draft content</option>
                  <option value="prepareCommunication">Prepare communication</option>
                </select>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Prompt</span>
                <textarea
                  className={styles.textarea}
                  value={form.prompt}
                  onChange={(event) => onFieldChange("prompt", event.target.value)}
                  data-testid="context-preview-prompt"
                />
              </label>
              <label className={styles.checkboxRow}>
                <input
                  className={styles.checkbox}
                  type="checkbox"
                  checked={form.sendRequestAttempted}
                  onChange={(event) => onFieldChange("sendRequestAttempted", event.target.checked)}
                  data-testid="context-preview-send-attempt"
                />
                <span>Send-attempt probe</span>
              </label>
              <button className={styles.button} type="submit" disabled={phase === "loading"} data-testid="context-preview-submit">
                <Eye className={styles.buttonIcon} size={18} />
                {buttonTextByPhase[phase]}
              </button>
            </div>
          </form>

          <div className={styles.panel}>
            <div className={statusClassByPhase[phase]} data-testid="context-preview-status">
              <h2 className={styles.statusHeading}>{statusTitleByPhase[phase]}</h2>
              <p className={styles.statusText}>{message}</p>
            </div>
            {resultView[phase]}
          </div>

          <section className={styles.panel} data-testid="document-supply-controls" aria-labelledby="document-supply-heading">
            <div className={supplyStatusClassByPhase[supplyPhase]} data-testid="document-supply-status" data-page-state={supplyPageStateByPhase[supplyPhase]}>
              <h2 className={styles.statusHeading} id="document-supply-heading">{supplyStatusTitleByPhase[supplyPhase]}</h2>
              <p className={styles.statusText}>{supplyMessage}</p>
            </div>
            <div className={styles.form}>
              <label className={styles.field}>
                <span className={styles.label}>Local Document</span>
                <input
                  className={styles.input}
                  type="file"
                  onChange={(event) =>
                    onSupplyFieldChange(
                      "selectedDocument",
                      event.currentTarget.files
                        ? selectedFileOption(event.currentTarget.files)
                        : Option.none<File>(),
                    )}
                  data-testid="document-supply-file"
                />
              </label>
              <div className={styles.selectionSummary} data-testid="document-supply-selected-document">
                {Option.renderOptionText(
                  supplyForm.selectedDocument,
                  "No local document selected",
                  (document) => `${document.name} (${document.size} bytes)`,
                )}
              </div>
              <label className={styles.field}>
                <span className={styles.label}>Document Name</span>
                <input
                  className={styles.input}
                  value={supplyForm.displayName}
                  onChange={(event) => onSupplyFieldChange("displayName", event.target.value)}
                  data-testid="document-supply-display-name"
                />
              </label>
              <div className={styles.selectionSummary} data-testid="document-supply-source-handle">
                {supplyForm.sourceHandle}
              </div>
              <button
                className={styles.button}
                type="button"
                disabled={supplyBusy}
                onClick={() => {
                  onSupplyUpload().catch(() => undefined);
                }}
                data-testid="document-supply-upload"
              >
                Add Listing Document
              </button>
              <label className={styles.field}>
                <span className={styles.label}>Supply ID</span>
                <input
                  className={styles.input}
                  value={supplyForm.statusSupplyId}
                  onChange={(event) => onSupplyFieldChange("statusSupplyId", event.target.value)}
                  data-testid="document-supply-status-id"
                />
              </label>
              <button
                className={styles.button}
                type="button"
                disabled={supplyBusy}
                onClick={() => {
                  onSupplyStatus().catch(() => undefined);
                }}
                data-testid="document-supply-refresh-status"
              >
                Refresh Supply Status
              </button>
              <div className={styles.emailGate} data-testid="document-supply-email-gate">
                Email attachment registration is hidden until connector context supplies message handle, attachment name, received timestamp, and listing context.
              </div>
              {acceptedView}
              {statusView}
            </div>
          </section>
        </div>
      </div>
    </section>
  );
};

export default ContextPreviewConsolePanel;
